export interface RetryOptions {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
}

export const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

export const withRetry = async <T>(
  fn: () => Promise<T>,
  options: RetryOptions = {
    maxAttempts: 3,
    baseDelay: 1000,
    maxDelay: 10000
  },
  onRetry?: (attempt: number, maxAttempts: number) => void,
  attempt = 1
): Promise<T> => {
  try {
    return await fn();
  } catch (error) {
    if (attempt >= options.maxAttempts) {
      throw error;
    }

    // Check if error is retryable
    if (!isRetryable(error)) {
      throw error;
    }

    // Exponential backoff: 1s, 2s, 4s, etc.
    const delay = Math.min(
      options.baseDelay * Math.pow(2, attempt - 1),
      options.maxDelay
    );

    // Notify about retry
    if (onRetry) {
      onRetry(attempt + 1, options.maxAttempts);
    }

    await sleep(delay);
    return withRetry(fn, options, onRetry, attempt + 1);
  }
};

export const isRetryable = (error: unknown): boolean => {
  // Network errors are retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  // HTTP errors: retry on 429 (rate limit) and 5xx (server errors)
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return status === 429 || status >= 500;
  }

  return false;
};

export class RetryableError extends Error {
  constructor(
    message: string,
    public status: number
  ) {
    super(message);
    this.name = 'RetryableError';
  }
}
